import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCashFlowAccounts, getLedger } from '../../../../services/treasuryAPI';
import { isDateInSelectedPeriod } from '../../../placeholders/periodSummaryUtils';
import AccountingLedgerTable3 from './AccountingLedgerTable3';

const AccountingLedgerPanel = ({
  focusedAccountIds = [],
  dateFrom: periodDateFrom = '',
  dateTo: periodDateTo = '',
  selectedPeriod = null,
}) => {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [dateFrom, setDateFrom] = useState(periodDateFrom || '');
  const [dateTo, setDateTo] = useState(periodDateTo || '');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setDateFrom(periodDateFrom || '');
    setDateTo(periodDateTo || '');
  }, [periodDateFrom, periodDateTo]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const params = {};
      if (accountId) params.chart_account_id = accountId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [ledger, chart] = await Promise.all([getLedger(params), getCashFlowAccounts()]);
      setRows(Array.isArray(ledger) ? ledger : []);
      setAccounts(Array.isArray(chart) ? chart : []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el libro mayor.');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const focusedIdSet = useMemo(() => {
    if (accountId) return null;
    if (!Array.isArray(focusedAccountIds) || !focusedAccountIds.length) return null;
    return new Set(focusedAccountIds.map((id) => Number(id)));
  }, [focusedAccountIds, accountId]);

  const visibleRows = useMemo(() => {
    let next = rows;
    if (selectedPeriod?.mode === 'year_ytd') {
      next = next.filter((row) => isDateInSelectedPeriod(row.entry_date, selectedPeriod));
    }
    if (!focusedIdSet) return next;
    return next.filter((row) => focusedIdSet.has(Number(row.chart_account_id)));
  }, [rows, focusedIdSet, selectedPeriod]);

  const subtitleOverride = useMemo(() => {
    if (accountId || !focusedIdSet) return undefined;
    const n = focusedIdSet.size;
    return `Filtrado desde el gráfico · ${n} cuenta${n === 1 ? '' : 's'}`;
  }, [accountId, focusedIdSet]);

  const periodLocked = Boolean(periodDateFrom || periodDateTo);

  return (
    <AccountingLedgerTable3
      rows={visibleRows}
      accounts={accounts}
      loading={isLoading}
      error={error}
      tableTitle="Libro mayor"
      subtitleOverride={subtitleOverride}
      accountId={accountId}
      dateFrom={dateFrom}
      dateTo={dateTo}
      onAccountChange={setAccountId}
      onDateFromChange={periodLocked ? undefined : setDateFrom}
      onDateToChange={periodLocked ? undefined : setDateTo}
      periodLocked={periodLocked}
    />
  );
};

export default AccountingLedgerPanel;
